import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowUpRight } from "lucide-react";

const CTA_URL = "https://diyb.ca/contact-diyb/";
const POINTING_IMG = "/images/paul-pointing.png";

export default function FinalCTA() {
  return (
    <section
      data-testid="final-cta-section"
      className="relative py-24 md:py-36 bg-[#0a192f] overflow-hidden">

      <div className="blueprint-grid absolute inset-0 opacity-10" />

      {/* Gold accent lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />

      {/* Pointing figure - slides in from left */}
      <motion.div
        className="hidden md:block absolute left-4 lg:left-8 top-[20%] z-20 pointer-events-none"
        initial={{ opacity: 0, x: -100, scale: 0.9 }}
        whileInView={{ opacity: 1, x: 0, scale: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ 
          duration: 0.8, 
          delay: 0.3, 
          ease: "easeOut"
        }}
      >
        <img
          src={POINTING_IMG}
          alt="Paul Hughes pointing"
          className="h-[300px] md:h-[380px] lg:h-[480px] xl:h-[520px] w-auto object-contain drop-shadow-2xl"
        />
      </motion.div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center md:ml-auto md:mr-8 lg:mr-16 xl:mr-24">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="font-body text-fluid-caption tracking-[0.3em] uppercase text-gold mb-6">

          Who This Is For
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-heading font-bold text-fluid-h2 uppercase tracking-tight text-slate-50 mb-8">

          Ready to Build Your
          <br />
          <span className="text-gold-gradient">Smart Digital Doorway?</span>
        </motion.h2>

        <motion.div
          className="space-y-4 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}>

          {[
          "If your systems feel loose, I can tighten them up.",
          "If your site feels quiet, I can make it work.",
          "If you feel stuck, I can build the next step.",
          "If you want a clean install, I can deliver it."].
          map((line, i) =>
          <motion.p
            key={i}
            className="font-body text-fluid-body-lg text-slate-400"
            initial={{ opacity: 0, x: -15 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}>

              {line}
            </motion.p>
          )}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="font-body text-fluid-body text-slate-500 mb-8">

          This is for owners. Solo operators. Small teams. People who want things done right.
          <br />
          <span className="text-slate-400">Kingsville, Ontario and the Windsor-Essex Region.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7, duration: 0.6 }}>

          <a href={CTA_URL} target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="final-cta-button"
              className="bg-gold hover:bg-gold-300 text-navy-500 font-heading font-bold tracking-wider uppercase text-lg px-12 py-7 rounded-sm transition-all duration-300 hover:shadow-[0_0_35px_-5px_rgba(212,175,55,0.6)] animate-glow-pulse group">

              Get Started Now!
              <ArrowUpRight size={20} className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Button>
          </a>
        </motion.div>
      </div>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
    </section>);

}